import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Check, ChevronDown, MessageSquarePlus, PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { copilotGreeting, filterConversations } from "../lib/copilot";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuSeparator,
  ResponsiveDropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";
import useIsMobile from "../hooks/useIsMobile";
import {
  MobileSheet, MobileSheetBody, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle,
} from "../components/ui/mobile-sheet";
import CopilotLogo from "../components/copilot/CopilotLogo";
import { ConversationList, CopilotComposer, CopilotMessages, Suggestions } from "../components/copilot/CopilotChat";
import { useCopilotSession } from "../components/copilot/CopilotSession";

const SIDEBAR_KEY = "jamora.copilot.sidebar";

function readSidebarOpen() {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) !== "closed";
  } catch {
    return true;
  }
}

// The Copilote Jamora on its own page (/copilote), like a chat application: the person's conversations on the left (new one,
// search, recent ones), the conversation in the middle, or a welcome screen with a large question field when none is under way.
// It shows the SAME conversation as the bubble (CopilotSession): « Agrandir » in the bubble lands here mid-conversation.
export default function CopilotPage() {
  const session = useCopilotSession();
  if (!session) return <Navigate to="/" replace />; // module off, no assistant:use, or switched off for this account
  return <CopilotWorkspace session={session} />;
}

function CopilotWorkspace({ session }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { state, actions, inputRef } = session;
  const { messages, conversations, loadingHistory, conversationId, title, streaming, availability } = state;
  const { loadConversations, refreshAvailability, focusInput } = actions;
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  const [mobileList, setMobileList] = useState(false);
  const [search, setSearch] = useState("");
  const empty = messages.length === 0;
  const currentTitle = (!empty && title) || "Nouvelle conversation";
  const unavailable = availability && !availability.available;

  useEffect(() => {
    refreshAvailability();
    focusInput();
  }, [refreshAvailability, focusInput]);

  // The list is (re)read when it is unknown: on arrival, and after a new conversation was created by a question.
  useEffect(() => {
    if (conversations === null && !loadingHistory) loadConversations();
  }, [conversations, loadingHistory, loadConversations]);

  const toggleSidebar = () => {
    setSidebarOpen((value) => {
      try {
        window.localStorage.setItem(SIDEBAR_KEY, value ? "closed" : "open");
      } catch { /* a private window: the choice lasts for this visit only */ }
      return !value;
    });
  };

  const open = (id) => {
    setMobileList(false);
    actions.openConversation(id);
  };
  const startNew = () => {
    setMobileList(false);
    actions.newConversation();
  };

  const list = (compact) => (
    <ConversationList conversations={filterConversations(conversations, search)} loading={loadingHistory} onOpen={open}
      onDelete={actions.deleteConversation} currentId={conversationId} compact={compact} />
  );
  const searchField = (
    <div className="relative">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une conversation" aria-label="Rechercher une conversation"
        className="h-9 pl-8" data-testid="copilot-page-search" />
    </div>
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 bg-white" data-testid="copilot-page">
      {sidebarOpen && (
        <aside className="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-slate-50/60 md:flex" data-testid="copilot-page-sidebar">
          <div className="flex items-center gap-2 px-4 pb-2 pt-4">
            <CopilotLogo size={26} />
            <span className="flex-1 font-display text-sm font-semibold text-slate-900">Copilote Jamora</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleSidebar} aria-label="Masquer les conversations"
              data-testid="copilot-page-sidebar-toggle">
              <PanelLeftClose size={16} />
            </Button>
          </div>
          <div className="space-y-2 px-3 pb-3">
            <Button variant="outline" className="w-full justify-start gap-2 bg-white" onClick={startNew} disabled={streaming} data-testid="copilot-page-new">
              <MessageSquarePlus size={16} /> Nouvelle conversation
            </Button>
            {searchField}
          </div>
          <div className="px-4 pb-1 text-2xs font-semibold uppercase tracking-wider text-slate-400">Récentes</div>
          <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3" aria-label="Vos conversations" data-testid="copilot-page-conversations">
            {list(true)}
          </nav>
        </aside>
      )}

      <section className="flex min-w-0 flex-1 flex-col" aria-label="Conversation avec le Copilote" style={{ viewTransitionName: "copilot-surface" }}>
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-slate-100 px-3 md:px-4">
          {!sidebarOpen && (
            <Button variant="ghost" size="icon" className="hidden h-8 w-8 md:inline-flex" onClick={toggleSidebar} aria-label="Afficher les conversations"
              data-testid="copilot-page-sidebar-toggle">
              <PanelLeftOpen size={16} />
            </Button>
          )}
          <div className="min-w-0 flex-1">
            {isMobile ? (
              // Phone: the title opens the conversations sheet (search, delete) below.
              <button type="button" onClick={() => setMobileList(true)} className={TITLE_BUTTON} aria-haspopup="dialog"
                aria-label={`Conversation : ${currentTitle}. Changer de conversation`} data-testid="copilot-page-list-mobile">
                <span className="truncate" data-testid="copilot-page-title">{currentTitle}</span>
                <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
              </button>
            ) : (
              <ConversationSwitcher title={currentTitle} conversations={conversations} currentId={conversationId}
                onOpen={open} onNew={startNew} disabled={streaming} />
            )}
          </div>
          {(!sidebarOpen || !empty) && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={startNew} disabled={streaming} aria-label="Nouvelle conversation">
              <MessageSquarePlus size={16} />
            </Button>
          )}
        </div>

        {empty ? (
          // Start screen: greeting, the large question field, suggestions.
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-8" data-testid="copilot-page-welcome">
            <div className="w-full max-w-2xl space-y-6">
              <div className="flex items-center justify-center gap-3">
                <CopilotLogo size={40} />
                <h1 className="font-display text-2xl font-semibold text-slate-900 md:text-3xl">{copilotGreeting(user?.name)}</h1>
              </div>
              <p className="text-center text-sm text-slate-500">
                Vos prospects, tâches, missions, finances : posez votre question. Le Copilote ne voit que ce que vous avez le droit de voir.
              </p>
              <CopilotComposer state={state} actions={actions} inputRef={inputRef} large />
              {!unavailable && <Suggestions onPick={actions.ask} disabled={streaming} className="flex flex-wrap justify-center gap-2" />}
            </div>
          </div>
        ) : (
          <>
            <CopilotMessages state={state} actions={actions} hideEmpty className="px-4 py-6" innerClassName="mx-auto w-full max-w-3xl" />
            <div className="shrink-0 px-4 pb-4 pt-2">
              <div className="mx-auto w-full max-w-3xl">
                <CopilotComposer state={state} actions={actions} inputRef={inputRef} large />
              </div>
            </div>
          </>
        )}
      </section>

      {/* Phone: the conversations in a bottom sheet (DESIGN_SYSTEM § 23). */}
      <MobileSheet open={mobileList} onOpenChange={setMobileList}>
        <MobileSheetContent aria-describedby={undefined} data-testid="copilot-page-list-sheet">
          <MobileSheetHeader>
            <MobileSheetTitle>Vos conversations</MobileSheetTitle>
            <MobileSheetCloseButton />
          </MobileSheetHeader>
          <MobileSheetBody className="space-y-3">
            <Button variant="outline" className="w-full justify-start gap-2" onClick={startNew} disabled={streaming}>
              <MessageSquarePlus size={16} /> Nouvelle conversation
            </Button>
            {searchField}
            <div>{list(false)}</div>
          </MobileSheetBody>
        </MobileSheetContent>
      </MobileSheet>
    </div>
  );
}

const TITLE_BUTTON = "flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 "
  + "focus:outline-none focus-visible:ring-2 focus-visible:ring-jamora/30 disabled:opacity-50";

/** The conversation's title, which opens the list of the person's conversations to switch to another one (computer). */
function ConversationSwitcher({ title, conversations, currentId, onOpen, onNew, disabled }) {
  return (
    <ResponsiveDropdownMenu>
      <ResponsiveDropdownMenuTrigger asChild>
        <button type="button" className={TITLE_BUTTON} disabled={disabled} aria-label={`Conversation : ${title}. Changer de conversation`}
          data-testid="copilot-page-switcher">
          <span className="truncate" data-testid="copilot-page-title">{title}</span>
          <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
        </button>
      </ResponsiveDropdownMenuTrigger>
      <ResponsiveDropdownMenuContent title="Vos conversations" align="start" className="w-80" data-testid="copilot-page-switcher-menu">
        <div className="max-h-80 overflow-y-auto">
          {conversations === null && <div className="px-2 py-3 text-sm text-slate-500">Chargement…</div>}
          {conversations?.length === 0 && <div className="px-2 py-3 text-sm text-slate-500">Aucune conversation pour le moment.</div>}
          {conversations?.map((c) => (
            <ResponsiveDropdownMenuItem key={c.id} onClick={() => c.id !== currentId && onOpen(c.id)} className="gap-2"
              data-testid={`copilot-page-switch-${c.id}`}>
              <Check size={14} className={`shrink-0 ${c.id === currentId ? "text-jamora" : "invisible"}`} aria-hidden="true" />
              <span className={`truncate ${c.id === currentId ? "font-medium text-jamora" : ""}`}>{c.title}</span>
            </ResponsiveDropdownMenuItem>
          ))}
        </div>
        <ResponsiveDropdownMenuSeparator />
        <ResponsiveDropdownMenuItem onClick={onNew} className="gap-2">
          <MessageSquarePlus size={14} aria-hidden="true" /> Nouvelle conversation
        </ResponsiveDropdownMenuItem>
      </ResponsiveDropdownMenuContent>
    </ResponsiveDropdownMenu>
  );
}
